
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";

const LAST_UPDATED = "October 31, 2026";
const SUPPORT_EMAIL = "support@taptotable.co";

const sections = [
  {
    title: "1. Agreement to These Terms",
    paragraphs: [
      "These Terms of Service govern your access to and use of TapToTable, a service provided by Melted Port Studios LLC. By requesting a TapToTable Page, reviewing a TapToTable Page, activating a subscription, accessing a TapToTable Dashboard, or otherwise using the service, you agree to these Terms.",
      "If you use TapToTable on behalf of a restaurant, company, restaurant group, or other organization, you represent that you have authority to accept these Terms on its behalf.",
    ],
  },
  {
    title: "2. About TapToTable",
    paragraphs: [
      "TapToTable is a restaurant marketing service that provides hosted restaurant pages, Promotions, Events, Featured Items, customer action links, secure, easy-to-use TapToTable Dashboard access, customer activity and supported analytics, restaurant photo hosting, TapToTable hosting, and ongoing support.",
      "TapToTable offers three main campaign types: Offers / Promotions, Events, and Featured Items. Restaurants can use these campaigns to present special offers, promote upcoming events, showcase individual menu items, and direct customers toward appropriate next steps.",
      "Supported customer actions may include viewing menus, ordering online, calling, requesting directions, visiting restaurant websites, claiming promotional offers, accessing reservation systems, or viewing event details.",
      "TapToTable does not itself process restaurant food orders, complete reservations, redeem offers in person, sell event tickets, or process payments between restaurants and their customers unless expressly stated otherwise.",
    ],
  },
  {
    title: "3. Requesting Your TapToTable Page",
    paragraphs: [
      "Restaurants may request that TapToTable build a TapToTable Page before activating the paid service. No payment is required to submit a page request or review the page.",
      "Your TapToTable Page may be built using information you provide and publicly available restaurant information, including your website, menu, social media profiles, business listings, photographs, and other relevant details.",
      "You may request changes before activation. TapToTable may revise, limit, decline, deactivate, or remove a review page at any time.",
      "Submitting a request does not guarantee acceptance, a completed page, activation, or continued availability of a review page.",
    ],
  },
  {
    title: "4. Activation and Monthly Subscription",
    paragraphs: [
      "The standard TapToTable subscription is $59 per month for the first restaurant location. An eligible additional restaurant location under the same ownership may receive the additional-location rate of $39 per month.",
      "Each activated restaurant location has its own TapToTable Page, login, dashboard, and recurring subscription. A unified multi-location dashboard is not currently included.",
      "The additional-location rate is available only for eligible restaurants under the same ownership and must be arranged with TapToTable. It is not a standalone $39 subscription for an unrelated new customer. TapToTable may request information reasonably necessary to verify eligibility.",
      "The applicable monthly subscription price and recurring billing terms will be presented at checkout. Activating a location begins its recurring monthly subscription at the price accepted during checkout.",
      "Subscriptions automatically renew each billing period until canceled. By completing checkout, you authorize the recurring charges shown during checkout using your selected payment method.",
      "Taxes may be added when required. You are responsible for providing accurate billing and contact information.",
    ],
  },
  {
    title: "5. Multi-Location Restaurant Accounts",
    paragraphs: [
      "Restaurant owners operating multiple locations may request a TapToTable Page for each location. Additional-location pricing applies only when TapToTable confirms the locations qualify under the same ownership.",
      "Each restaurant location is managed through a separate login and dashboard. Campaigns, restaurant details, photographs, links, and supported analytics are maintained independently for each location.",
      "An additional restaurant location is not automatically activated or billed merely because you request it through the signup form. Each location must be separately arranged and activated under its applicable subscription.",
      "Changes in restaurant ownership or eligibility may affect the additional-location rate. Any resulting changes to future subscription pricing will be disclosed as required before they take effect.",
      "Cancellation of one restaurant location does not automatically cancel subscriptions for other locations. Each active subscription must be canceled separately unless TapToTable confirms otherwise.",
    ],
  },
  {
    title: "6. Payments and Payment Processing",
    paragraphs: [
      "TapToTable uses Stripe or another authorized payment processor to process subscription payments. TapToTable does not directly store your complete payment-card information.",
      "Payment processing may be subject to the payment provider's applicable terms and privacy practices.",
      "If a payment fails, is reversed, disputed, or remains unpaid, TapToTable or its payment processor may attempt additional collection, contact you about the billing issue, restrict dashboard access, pause the restaurant page, or terminate the affected subscription.",
      "Except where otherwise required by law, amounts already charged are non-refundable. TapToTable does not ordinarily provide prorated refunds or credits for unused time remaining in a paid billing period.",
    ],
  },
  {
    title: "7. Cancellation",
    paragraphs: [
      `You may cancel a recurring TapToTable subscription at any time by contacting ${SUPPORT_EMAIL} from an email address associated with your account or by using a Stripe billing-management or cancellation option made available for your subscription.`,
      "Under TapToTable's standard cancellation policy, cancellation takes effect at the end of the current paid billing period. Future renewals for the canceled subscription will stop after that period.",
      "You may continue using the paid service through the remaining billing period unless access is ended earlier because of misuse, nonpayment, legal requirements, or another violation of these Terms.",
      "For multi-location customers, each location has a separate recurring subscription. Canceling one location does not necessarily cancel another location.",
      "Cancellation does not automatically refund payments already processed. Except where required by law, TapToTable does not ordinarily provide prorated refunds or credits for unused time in the current billing period.",
      "If you need assistance identifying or canceling an active subscription, contact TapToTable support.",
    ],
  },
  {
    title: "8. Restaurant Information and Account Responsibilities",
    paragraphs: [
      "You are responsible for providing accurate and current restaurant, ownership, contact, billing, menu, ordering, website, phone, address, hours, campaign, and account information.",
      "You are responsible for safeguarding your TapToTable Dashboard credentials and for activity performed through your account. Notify TapToTable promptly if you believe your credentials have been compromised.",
      "You must have the necessary rights and authority to provide and publish restaurant information, images, logos, trademarks, menus, descriptions, links, Promotions, Events, Featured Items, and other materials submitted to TapToTable.",
      "You agree to review your TapToTable Page and promptly report incorrect, outdated, infringing, unlawful, or misleading content.",
      "For multi-location accounts, you are responsible for keeping the information and customer-facing campaigns for each restaurant location accurate and current.",
    ],
  },
  {
    title: "9. Promotions, Events, and Featured Items",
    paragraphs: [
      "TapToTable supports Offers / Promotions, Events, and Featured Items. The restaurant is responsible for selecting, approving, funding, administering, and honoring the campaigns it publishes.",
      "Offers and Promotions may provide customers with a claim function and a redemption code. The restaurant is responsible for honoring valid offers and communicating applicable restrictions, purchase requirements, expiration dates, and redemption conditions.",
      "Events may include dates, times, descriptions, reservation links, guest lists, ticketing links, or other customer actions. The restaurant is responsible for event accuracy, scheduling, availability, ticketing arrangements, and associated customer commitments.",
      "Featured Item campaigns allow restaurants to showcase dishes or menu products using photos, descriptions, and customer action links. Featured Items do not require a discount or a promotional claim code.",
      "Restaurants are responsible for accurately describing featured products, ingredients, availability, prices where stated, dietary information where applicable, and any other material information presented to customers.",
      "All Promotions, Events, Featured Items, and related customer-facing content must comply with applicable advertising, consumer-protection, food-service, alcohol, promotion, event, and other laws and regulations.",
      "TapToTable may refuse, edit, pause, or remove campaign content that appears unlawful, misleading, abusive, unsafe, inappropriate, or inconsistent with the service.",
    ],
  },
  {
    title: "10. Claims, Analytics, and Business Results",
    paragraphs: [
      "An offer claim indicates that a visitor interacted with the claim function. It does not establish that the visitor purchased food, visited the restaurant, redeemed the offer, or became a paying customer.",
      "Customer activity and supported analytics may include page visits, offer claims, event actions, claim rates, and certain supported customer interactions. Available analytics depend on the campaign type and features currently supported.",
      "Dedicated Featured Item button-click analytics may not be available. The appearance of a customer action button does not guarantee that a corresponding click metric is tracked.",
      "Analytics can be affected by browser settings, blocked scripts, automated traffic, connectivity, device behavior, duplicate activity, and technical limitations.",
      "TapToTable does not guarantee any minimum number of visits, calls, directions requests, orders, reservations, claims, event actions, redemptions, customers, sales, revenue, profit, or other business results.",
    ],
  },
  {
    title: "11. Customer Links and Third-Party Services",
    paragraphs: [
      "A TapToTable Page may link to third-party websites and services, including restaurant websites, online-ordering providers, menu platforms, mapping services, social-media services, reservation systems, ticketing providers, and payment services.",
      "TapToTable does not control and is not responsible for the availability, security, accuracy, content, policies, fees, transactions, or performance of third-party services.",
      "Your use of a third-party service is governed by that provider's terms and policies.",
    ],
  },
  {
    title: "12. License to Restaurant Content",
    paragraphs: [
      "You retain your ownership rights in content you provide to TapToTable.",
      "You grant Melted Port Studios LLC a non-exclusive, worldwide, royalty-free license to host, copy, format, resize, display, publish, and otherwise use submitted restaurant content as reasonably necessary to build and maintain your TapToTable Page and provide and support the service.",
      "This license continues for as long as reasonably necessary to provide the service, maintain required records, resolve disputes, comply with legal obligations, and protect TapToTable's rights.",
    ],
  },
  {
    title: "13. TapToTable Ownership",
    paragraphs: [
      "TapToTable, its software, page designs, dashboards, branding, code, systems, documentation, workflows, and other service materials are owned by Melted Port Studios LLC or its licensors.",
      "Except for the limited right to use the service under these Terms, no ownership interest in TapToTable or its technology is transferred to you.",
      "You may not copy, resell, sublicense, reverse engineer, interfere with, scrape, misuse, or attempt unauthorized access to TapToTable or its systems.",
    ],
  },
  {
    title: "14. Acceptable Use",
    paragraphs: [
      "You may not use TapToTable for unlawful, fraudulent, deceptive, infringing, abusive, discriminatory, harmful, or unauthorized purposes.",
      "You may not upload malware, attempt to disrupt the service, impersonate another business, submit information without authorization, manipulate analytics, or use TapToTable to distribute spam.",
      "TapToTable may investigate suspected misuse and may suspend or terminate access when reasonably necessary to protect restaurants, customers, third parties, the service, or Melted Port Studios LLC.",
    ],
  },
  {
    title: "15. Availability and Changes to the Service",
    paragraphs: [
      "TapToTable may update, improve, replace, limit, pause, or discontinue features. The service may occasionally be unavailable because of maintenance, updates, third-party outages, security issues, technical failures, or circumstances outside our reasonable control.",
      "TapToTable does not guarantee uninterrupted or error-free operation, permanent retention of analytics, or compatibility with every device, browser, platform, ordering provider, website, or third-party service.",
      "We may change future pricing or subscription features by providing reasonable notice when required. A price change will not retroactively alter charges already completed.",
    ],
  },
  {
    title: "16. Suspension and Termination",
    paragraphs: [
      "TapToTable may suspend, restrict, or terminate a restaurant page, dashboard, or account for nonpayment, suspected fraud, security concerns, unlawful activity, misuse, infringement, material inaccuracies, or violation of these Terms.",
      "You may stop using the service at any time, but stopping use does not by itself cancel recurring billing. You must submit a cancellation request using an available cancellation method.",
      "Sections that by their nature should survive termination will remain effective, including payment obligations, ownership provisions, disclaimers, limitations of liability, and dispute-related provisions.",
    ],
  },
  {
    title: "17. Disclaimer of Warranties",
    paragraphs: [
      "To the fullest extent permitted by law, TapToTable is provided on an “as is” and “as available” basis without warranties of any kind, whether express, implied, statutory, or otherwise.",
      "Melted Port Studios LLC disclaims implied warranties of merchantability, fitness for a particular purpose, title, non-infringement, and any warranty arising from course of dealing or usage of trade.",
      "Nothing in these Terms excludes a warranty or consumer right that cannot lawfully be excluded.",
    ],
  },
  {
    title: "18. Limitation of Liability",
    paragraphs: [
      "To the fullest extent permitted by law, Melted Port Studios LLC and its owners, employees, contractors, affiliates, and service providers will not be liable for indirect, incidental, special, exemplary, consequential, or punitive damages, or for lost profits, lost revenue, lost data, lost opportunities, business interruption, or reputational harm arising from or related to TapToTable.",
      "To the fullest extent permitted by law, the total aggregate liability of Melted Port Studios LLC arising from or relating to TapToTable will not exceed the amount you paid for TapToTable during the three months immediately preceding the event giving rise to the claim.",
      "Some jurisdictions do not permit certain exclusions or limitations, so portions of this section may not apply to you.",
    ],
  },
  {
    title: "19. Indemnification",
    paragraphs: [
      "To the extent permitted by law, you agree to defend, indemnify, and hold harmless Melted Port Studios LLC and its owners, employees, contractors, affiliates, and service providers from claims, losses, liabilities, damages, judgments, costs, and reasonable legal fees arising from your restaurant content, Promotions, Events, Featured Items, offers, business practices, use of the service, violation of these Terms, or infringement of another party's rights.",
    ],
  },
  {
    title: "20. Privacy",
    paragraphs: [
      "Your use of TapToTable is also subject to the TapToTable Privacy Policy. You should review that policy to understand how information may be collected, used, stored, and shared.",
    ],
  },
  {
    title: "21. Changes to These Terms",
    paragraphs: [
      "We may revise these Terms as TapToTable changes. The updated version will be posted with a revised effective date.",
      "Your continued use of the service after updated Terms become effective constitutes acceptance of the revised Terms to the extent permitted by law. If you do not agree to an update, you should stop using the service and cancel any active subscription.",
    ],
  },
  {
    title: "22. Contact",
    paragraphs: [
      `Questions about these Terms, billing, cancellation, additional restaurant locations, or TapToTable may be sent to ${SUPPORT_EMAIL}.`,
    ],
  },
];

export default function Terms() {
  return (
    <div
      className="min-h-screen bg-[#09090B] text-white"
      data-testid="terms-page"
    >
      <Navbar />

      <main className="px-6 pb-24 pt-32 md:px-12">
        <motion.article
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="mx-auto max-w-4xl"
        >
          <Link
            to="/"
            className="mb-6 inline-flex items-center gap-1 text-xs text-zinc-500 transition-colors hover:text-white"
            data-testid="terms-back-link"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M15 18l-6-6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back to home
          </Link>

          <div className="label-caps">Legal</div>

          <h1
            className="mt-3 font-display text-3xl font-semibold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl"
            data-testid="terms-heading"
          >
            Terms of Service
          </h1>

          <p className="mt-4 text-sm text-zinc-500">
            Last updated: {LAST_UPDATED}
          </p>

          <div className="mt-8 rounded-2xl border border-[#00E5FF]/25 bg-[#00E5FF]/10 p-5">
            <h2 className="font-display text-lg font-semibold text-white">
              Key service terms
            </h2>

            <ul className="mt-4 space-y-3 text-sm leading-relaxed text-zinc-300">
              {[
                "We build your TapToTable Page before you pay.",
                "First restaurant location: $59/month.",
                "Each eligible additional location: $39/month.",
                "Each restaurant location has its own page, login, dashboard, and subscription.",
                "Subscriptions renew monthly until canceled.",
                "Restaurants are responsible for their Promotions, Events, Featured Items, and business information.",
                "TapToTable does not guarantee visits, sales, or revenue.",
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3"
                >
                  <span className="mt-0.5 text-[#00E5FF]">
                    ✓
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10 space-y-10 text-base leading-relaxed text-zinc-300">
            {sections.map((section) => (
              <section key={section.title}>
                <h2 className="font-display text-xl font-semibold text-white md:text-2xl">
                  {section.title}
                </h2>

                <div className="mt-4 space-y-4">
                  {section.paragraphs.map((paragraph) => (
                    <p key={paragraph}>
                      {paragraph}
                    </p>
                  ))}
                </div>

                {section.title === "20. Privacy" && (
                  <Link
                    to="/privacy"
                    className="mt-4 inline-flex text-sm font-semibold text-[#00E5FF] transition-colors hover:text-[#66FFFF]"
                  >
                    Read the Privacy Policy →
                  </Link>
                )}

                {section.title === "22. Contact" && (
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="mt-4 inline-flex text-sm font-semibold text-[#00E5FF] transition-colors hover:text-[#66FFFF]"
                    data-testid="terms-contact-email"
                  >
                    {SUPPORT_EMAIL}
                  </a>
                )}
              </section>
            ))}
          </div>

          <div className="mt-12 rounded-2xl border border-white/10 bg-[#121214] p-5 text-sm leading-relaxed text-zinc-400">
            By requesting a TapToTable Page, activating your subscription, or
            continuing to use TapToTable, you acknowledge that you have read
            and agree to these Terms of Service.
          </div>
        </motion.article>
      </main>

      <Footer />
    </div>
  );
}
